import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getComparableQualityLatest,
  getComparableQualityIssues,
  runComparableQualityNow,
} from "../../api/adminApi";
import { Section, StatCard, ShortId, fmtDate } from "./AdminUI";

/**
 * Batch 5a Section E — Comparable Quality card for the Quality Dashboard.
 *
 * Surfaces the daily Comparable Quality Worker (Section D):
 *   • Score (1.0 = perfect, drops with each bad-comparable pair).
 *   • Severity bucket (ok / issue / severe / critical) driven by the
 *     `comparable_quality_issue_threshold` runtime flag.
 *   • Per-issue-type breakdown (fuel_mismatch, generation_mismatch …).
 *   • Drilldown: click an issue-type chip to list up to 25 example
 *     pairs (subject summary + comparable summary) with the configured
 *     mismatch detail.
 *
 * Polling: 60 s normally, 5 s while a run-now job is in flight.
 */
export default function ComparableQualityCard() {
  const qc = useQueryClient();
  const [activeIssueType, setActiveIssueType] = useState(null);
  const [activeVertical, setActiveVertical] = useState(null);
  const [running, setRunning] = useState(false);

  const latestQ = useQuery({
    queryKey: ["admin", "comparable-quality", "latest"],
    queryFn: getComparableQualityLatest,
    refetchInterval: running ? 5_000 : 60_000,
    refetchOnWindowFocus: !running,
  });

  // Drilldown — only fires when the user clicks an issue-type chip.
  const issuesQ = useQuery({
    queryKey: [
      "admin", "comparable-quality", "issues",
      activeIssueType, activeVertical,
    ],
    queryFn: () => getComparableQualityIssues({
      issue_type: activeIssueType,
      vertical: activeVertical,
      limit: 25,
    }),
    enabled: !!(activeIssueType || activeVertical),
  });

  const runMut = useMutation({
    mutationFn: runComparableQualityNow,
    onSuccess: (data) => {
      setRunning(true);
      toast.info(
        `Comparable-Quality run queued: ${data?.report_ulid?.slice(0, 8)}…`,
        { id: "cq-queued" },
      );
    },
    onError: (e) => {
      toast.error(`Run-Now fehlgeschlagen: ${e?.message || e}`);
    },
  });

  // Stop fast-poll once the next /latest reflects a terminal status that
  // is fresher than the last `started_at` we saw.
  useEffect(() => {
    if (!running) return;
    const status = latestQ.data?.status;
    if (status && status !== "running") {
      setRunning(false);
      qc.invalidateQueries({ queryKey: ["admin", "comparable-quality"] });
      toast.success(
        `Comparable-Quality fertig — score=${
          (latestQ.data?.overall_quality_score ?? 0).toFixed(2)
        }`,
        { id: "cq-done" },
      );
    }
  }, [latestQ.data, running, qc]);

  const data = latestQ.data;

  // Sort issue types by count desc. Stats inlined inside the memo so
  // ESLint exhaustive-deps doesn't complain about the `|| {}` fallback.
  const ranked = useMemo(() => {
    const stats = data?.stats || {};
    return Object.entries(stats)
      .map(([k, v]) => ({ type: k, count: v }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  if (latestQ.isLoading && !latestQ.data) {
    return (
      <Section
        title="Comparable Quality"
        testid="cq-section"
        action={<span className="text-xs text-zinc-500">/api/admin/comparable-quality</span>}
      >
        <div className="text-zinc-500" data-testid="cq-loading">
          Lade Comparable-Quality-Daten…
        </div>
      </Section>
    );
  }
  if (latestQ.isError) {
    return (
      <Section title="Comparable Quality" testid="cq-section">
        <div className="text-rose-400" data-testid="cq-error">
          Error: {String(latestQ.error?.message || latestQ.error)}
        </div>
      </Section>
    );
  }

  const hasReport = data?.has_report;
  const score = data?.overall_quality_score;
  const severity = data?.severity || "ok";
  const sampled = data?.analyses_sampled || {};
  const status = data?.status;
  const pairs = data?.pairs_compared;

  const severityCls = mapSeverityToBadge(severity);

  return (
    <Section
      title="Comparable Quality"
      testid="cq-section"
      action={
        <div className="flex items-center gap-3">
          {running && (
            <div
              data-testid="cq-running-banner"
              className="px-3 py-1 rounded-md text-xs border border-sky-500/40 bg-sky-500/10 text-sky-200"
            >
              Running…
            </div>
          )}
          <button
            type="button"
            data-testid="cq-run-now-btn"
            onClick={() => runMut.mutate()}
            disabled={runMut.isPending || running}
            className="px-3 py-1.5 rounded-md text-xs border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {runMut.isPending ? "Queueing…" : "Run now"}
          </button>
        </div>
      }
    >
      <div className="space-y-4" data-testid="cq-content">
        {/* ----- top stats ----- */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cq-top-stats">
          <div
            data-testid="cq-stat-severity"
            className="rounded-xl border border-zinc-800 bg-zinc-950/50 px-4 py-3"
          >
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              Severity
            </div>
            <div className="text-2xl text-zinc-100 font-medium tabular-nums mt-1">
              {hasReport ? (
                <span
                  className={`px-2 py-0.5 rounded text-base uppercase tracking-wider border ${severityCls}`}
                >
                  {severity}
                </span>
              ) : (
                "—"
              )}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              {hasReport
                ? `as of ${fmtDate(data?.finished_at || data?.run_at)}`
                : "noch keine Läufe"}
            </div>
          </div>
          <StatCard
            label="Quality score"
            value={score != null ? score.toFixed(3) : "—"}
            hint={hasReport ? "1.0 = perfekt" : ""}
            testid="cq-stat-score"
          />
          <StatCard
            label="Pairs compared"
            value={pairs ?? "—"}
            hint={
              hasReport
                ? `auto ${sampled.auto ?? 0} · immo ${sampled.real_estate ?? 0}`
                : ""
            }
            testid="cq-stat-pairs"
          />
          <StatCard
            label="Status"
            value={status || "—"}
            hint={
              data?.duration_ms != null
                ? `${(data.duration_ms / 1000).toFixed(1)} s`
                : ""
            }
            testid="cq-stat-status"
          />
        </div>

        {/* ----- run-summary line ----- */}
        <div className="text-xs text-zinc-500" data-testid="cq-meta">
          {hasReport
            ? `${data?.trigger || "—"} · ULID `
            : "Klicke „Run now“ um den ersten Lauf zu starten."}
          {hasReport && <ShortId value={data?.ulid} />}
        </div>

        {/* ----- breakdown chips ----- */}
        {ranked.length > 0 ? (
          <div data-testid="cq-breakdown">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Issues nach Typ
            </div>
            <div className="flex flex-wrap gap-2">
              {ranked.map((row) => {
                const isActive = activeIssueType === row.type;
                return (
                  <button
                    key={row.type}
                    type="button"
                    data-testid={`cq-issue-chip-${row.type}`}
                    onClick={() => {
                      setActiveIssueType(isActive ? null : row.type);
                      setActiveVertical(null);
                    }}
                    className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                      isActive
                        ? "border-amber-400/50 bg-amber-400/20 text-amber-100"
                        : "border-zinc-700 bg-zinc-900/50 text-zinc-300 hover:bg-zinc-800/60"
                    }`}
                  >
                    {row.type}{" "}
                    <span className="ml-1 text-zinc-500 tabular-nums">
                      {row.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : hasReport ? (
          <div
            className="text-xs text-emerald-300/80"
            data-testid="cq-no-issues"
          >
            Keine Comparable-Issues im letzten Sample erkannt 👌
          </div>
        ) : null}

        {/* ----- drilldown panel ----- */}
        {activeIssueType && (
          <ComparableQualityDrilldown
            issueType={activeIssueType}
            items={issuesQ.data?.items || []}
            isLoading={issuesQ.isLoading}
            onClose={() => setActiveIssueType(null)}
          />
        )}
      </div>
    </Section>
  );
}

// --------------------------------------------------------------------------
// Drilldown panel — lists up to 25 example pairs for the selected issue
// type. Each row shows the subject summary, the comparable summary, and
// the mismatch detail. PII-free by construction (worker writes only
// public listing fields).
// --------------------------------------------------------------------------
function ComparableQualityDrilldown({ issueType, items, isLoading, onClose }) {
  return (
    <div
      data-testid={`cq-drilldown-${issueType}`}
      className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-amber-100">
          Issue drilldown:{" "}
          <span className="font-mono text-xs bg-amber-500/15 px-1.5 py-0.5 rounded">
            {issueType}
          </span>{" "}
          <span className="text-amber-200/60 text-xs ml-1">
            ({items.length})
          </span>
        </div>
        <button
          type="button"
          data-testid="cq-drilldown-close"
          onClick={onClose}
          className="text-xs text-zinc-400 hover:text-zinc-200"
        >
          Schließen
        </button>
      </div>

      {isLoading ? (
        <div className="text-xs text-zinc-500">Lade Beispiele…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-zinc-500">
          Keine Issues mit diesem Typ im aktuellen Report.
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {items.map((it, i) => (
            <div
              key={`${it.subject_listing_ulid}-${it.comparable_listing_ulid}-${i}`}
              data-testid={`cq-issue-row-${i}`}
              className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs"
            >
              <PairSummary
                title="Subject"
                ulid={it.subject_listing_ulid}
                summary={it.subject_summary}
                testid={`cq-issue-${i}-subject`}
              />
              <PairSummary
                title="Comparable"
                ulid={it.comparable_listing_ulid}
                summary={it.comparable_summary}
                testid={`cq-issue-${i}-comparable`}
              />
              {it.detail && (
                <div
                  className="md:col-span-2 text-[11px] text-amber-200/90 tabular-nums"
                  data-testid={`cq-issue-${i}-detail`}
                >
                  Mismatch: {it.detail}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PairSummary({ title, ulid, summary, testid }) {
  const s = summary || {};
  // Render only public fields — the API guarantees no seller PII here.
  const lines = [];
  if (s.title) lines.push(s.title);
  const meta = [];
  if (s.make || s.model_family) {
    meta.push([s.make, s.model_family, s.trim].filter(Boolean).join(" "));
  }
  if (s.year) meta.push(`${s.year}`);
  if (s.power_kw) meta.push(`${s.power_kw} kW`);
  if (s.fuel) meta.push(s.fuel);
  if (s.gearbox) meta.push(s.gearbox);
  if (s.body_type) meta.push(s.body_type);
  if (s.drive) meta.push(s.drive);
  if (s.object_type) meta.push(s.object_type);
  if (s.living_area_m2) meta.push(`${s.living_area_m2} m²`);
  if (s.rooms) meta.push(`${s.rooms} Zi`);
  if (s.postal_code) meta.push(`${s.postal_code} ${s.city || ""}`.trim());
  if (s.condition) meta.push(s.condition);
  if (s.is_new_build) meta.push("new build");
  const price = s.price
    ? new Intl.NumberFormat("de-AT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(s.price)
    : null;

  return (
    <div data-testid={testid} className="space-y-1">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
        {title} · <ShortId value={ulid} />
      </div>
      {lines[0] && (
        <div className="text-zinc-200 truncate" title={lines[0]}>
          {lines[0]}
        </div>
      )}
      {meta.length > 0 && (
        <div className="text-zinc-400 truncate" title={meta.join(" · ")}>
          {meta.join(" · ")}
        </div>
      )}
      {price && <div className="text-zinc-300 tabular-nums">{price}</div>}
    </div>
  );
}

function mapSeverityToBadge(severity) {
  switch (severity) {
    case "ok":
      return "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
    case "issue":
      return "bg-amber-500/20 text-amber-200 border-amber-500/40";
    case "severe":
      return "bg-orange-500/20 text-orange-200 border-orange-500/40";
    case "critical":
      return "bg-rose-500/20 text-rose-300 border-rose-500/40";
    default:
      return "bg-zinc-700/30 text-zinc-400 border-zinc-700";
  }
}
